// ============================================================
// FILE: src/pages/Services.jsx  →  SERVICES PAGE
// Shows all 12 trade services + the 10-step process strip.
// Service text is in src/data/site.js (SERVICES LIST).
// ============================================================

import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { useSEO } from "../hooks/useSEO";
import { SERVICES, PROCESS_STEPS } from "../data/site";

export default function Services() {
  useSEO("Services — Evolve Trade & Logistics", "Global sourcing, export, import, procurement, supplier verification, quality inspection, documentation, logistics, warehousing, packaging, private labelling and consulting.");
  return (
    <>
      <PageHero
        label="Trade Services"
        title="Every mile of the journey,"
        accent="one accountable partner"
        description="Twelve integrated services that take your cargo from a supplier's factory floor to your customer's door — verified, inspected, documented and tracked."
        crumbs={[{ label: "Services" }]}
      />

      <section className="pb-24" data-testid="services-grid">
        <div className="container-x">
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <StaggerItem key={s.title}>
                <div className="card-surface group relative h-full overflow-hidden" data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="absolute right-6 top-6 font-display text-sm font-bold tracking-widest text-slate-900/10 transition-colors duration-500 group-hover:text-teal-500/40 dark:text-white/10">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-700/15 to-teal-500/15">
                    <s.icon className="h-6 w-6 text-teal-500" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-semibold tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="pb-24 sm:pb-32" data-testid="services-process-strip">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] border border-teal-500/20 bg-gradient-to-br from-blue-900 via-navy to-slate-950 px-8 py-14 sm:px-14">
              <div className="bg-grid pointer-events-none absolute inset-0 opacity-40" />
              <div className="relative">
                <h2 className="max-w-xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  From inquiry to delivery in ten transparent steps
                </h2>
                <div className="mt-8 flex flex-wrap gap-2.5">
                  {PROCESS_STEPS.map((s, i) => (
                    <span key={s.title} className="glass rounded-full px-4 py-2 text-xs font-medium text-slate-200" data-testid={`process-chip-${i}`}>
                      <span className="mr-1.5 font-bold text-teal-400">{String(i + 1).padStart(2, "0")}</span>
                      {s.title}
                    </span>
                  ))}
                </div>
                <Link to="/contact" className="btn-primary mt-10" data-testid="services-cta">
                  Start the Process <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
